// ============================================
// OFFLINE SYNC ENGINE
// src/services/offlineSyncService.js
// Zeno ERP v18 — 16 Jun 2026
// Replaces: serviceTaskService.syncOfflineQueue
// ============================================

import { db } from "../firebase";
import {
  doc, getDoc, updateDoc, addDoc, getDocs,
  collection, query, where, serverTimestamp, increment,
} from "firebase/firestore";

import {
  getUnsyncedActions,
  getUnsyncedReports,
  markActionSynced,
  markReportSynced,
  incrementRetry,
} from "./offlineDB";

import {
  SERVICE_STATUS,
  SERVICE_COLLECTIONS,
  COMPLETION_TYPE,
} from "../constants/enums/serviceEnums";

const TASKS  = SERVICE_COLLECTIONS.TASKS;
const VISITS = SERVICE_COLLECTIONS.VISITS;
const LOGS   = SERVICE_COLLECTIONS.LOGS;

const MAX_RETRIES = 3;

// ─────────────────────────────────────────────
// INTERNAL — LOG
// ─────────────────────────────────────────────
// D-298: company_id added — service_logs create rule needs it
// (writingSameCompany()); without it every log write was denied silently.
const logStatusChange = async (taskId, { company_id = "", from, to, changed_by, changed_by_name, reason = "" }) => {
  try {
    await addDoc(collection(db, LOGS), {
      company_id:      company_id || "",
      task_id:         taskId,
      from_status:     from,
      to_status:       to,
      changed_by:      changed_by      || "",
      changed_by_name: changed_by_name || "",
      reason:          reason          || "",
      created_at:      serverTimestamp(),
    });
  } catch (err) {
    console.error("logStatusChange error:", err);
  }
};

// ─────────────────────────────────────────────
// SYNC SINGLE ACTION → Firestore
// ─────────────────────────────────────────────
const syncAction = async (action) => {
  const { service_id, action: type, payload } = action;
  const taskRef  = doc(db, TASKS, service_id);
  const taskSnap = await getDoc(taskRef);
  if (!taskSnap.exists()) throw new Error(`Task ${service_id} not found`);
  const data = taskSnap.data();
  if (payload.company_id && data.company_id && payload.company_id !== data.company_id) {
    throw new Error(`Access denied — task ${service_id} belongs to a different company (stale offline queue entry)`);
  }

  // ── ACCEPT ──────────────────────────────
  if (type === "accept") {
    const techs = (data.assigned_techs || []).map(t =>
      t.id === payload.uid
        ? { ...t, accepted: true, accepted_at: payload.timestamp }
        : t
    );
    await updateDoc(taskRef, {
      assigned_techs: techs,
      updated_at:     serverTimestamp(),
    });
    return;
  }

  // ── START ───────────────────────────────
  if (type === "start") {
    // D-298: a retried sync must not start the job twice.
    if (data.offline_start_at && data.offline_start_at === payload.timestamp) return;

    await updateDoc(taskRef, {
      status:                   SERVICE_STATUS.IN_PROGRESS,
      started_at:               serverTimestamp(),
      updated_at:               serverTimestamp(),
      start_attempt_at:         payload.timestamp,
      start_location:           payload.coords || null,
      start_location_at:        payload.timestamp,
      start_location_timestamp: serverTimestamp(),
      location_skipped:         payload.location_skipped === "manager_initiated"
                                  ? "manager_initiated"
                                  : (payload.coords ? null : "offline"),
      synced_from_offline:      true,
      offline_start_at:         payload.timestamp,
      // D-298: same audit fields as the online startWork()
      started_by:               payload.filled_by      || payload.uid  || "",
      started_by_name:          payload.filled_by_name || payload.name || "",
      started_by_role:          payload.filled_by_role || "",
    });

    // Create visit log
    await addDoc(collection(db, VISITS), {
      task_id:          service_id,
      company_id:       payload.company_id || data.company_id || "",
      tech_id:          payload.uid        || "",
      tech_name:        payload.name       || "",
      check_in_at:      serverTimestamp(),
      check_out_at:     null,
      duration_minutes: null,
      start_location:   payload.coords || null,
      created_at:       serverTimestamp(),
      synced_from_offline: true,
      offline_start_at: payload.timestamp || null, // real start time (check_in_at = sync time)
    });

    await logStatusChange(service_id, {
      company_id:      data.company_id,
      from:            data.status,
      to:              SERVICE_STATUS.IN_PROGRESS,
      changed_by:      payload.filled_by      || payload.uid,
      changed_by_name: payload.filled_by_name || payload.name,
      reason:          "Synced from offline",
    });
    return;
  }

  // ── COMPLETE ─────────────────────────────
  if (type === "complete") {
    const endISO = payload.timestamp;

    // D-298: a retried sync must not complete the job twice (the merge
    // below would otherwise add the same photos / items again).
    if (data.offline_completed_at && data.offline_completed_at === endISO) return;

    // D-298: close the open visit and ADD its minutes to the task total —
    // same ledger-first accounting as the online endJob(). Before this,
    // duration_minutes was overwritten with null (start_time_iso is never
    // in the action payload) and the visit stayed open for ever.
    let thisVisitDurationMinutes = 0;
    try {
      const openVisitSnap = await getDocs(query(
        collection(db, VISITS),
        where("company_id", "==", data.company_id || payload.company_id || ""),
        where("task_id", "==", service_id),
        where("check_out_at", "==", null)
      ));
      if (!openVisitSnap.empty) {
        const visitDoc  = openVisitSnap.docs[0];
        const visitData = visitDoc.data();
        const checkIn   = visitData.offline_start_at
          ? new Date(visitData.offline_start_at)
          : (visitData.check_in_at?.toDate ? visitData.check_in_at.toDate() : new Date(visitData.check_in_at));
        const mins = Math.round((new Date(endISO) - checkIn) / 60000);
        thisVisitDurationMinutes = Number.isFinite(mins) ? Math.max(0, mins) : 0;
        await updateDoc(doc(db, VISITS, visitDoc.id), {
          check_out_at:         serverTimestamp(),
          offline_check_out_at: endISO,
          duration_minutes:     thisVisitDurationMinutes,
        });
      }
    } catch (visitErr) {
      console.warn("[Sync] Visit duration accounting failed (non-blocking):", visitErr);
    }

    const finalStatus = payload.completion_type === COMPLETION_TYPE.COMPLETE
      ? SERVICE_STATUS.COMPLETED_PENDING_APPROVAL
      : SERVICE_STATUS.PARTIAL;

    await updateDoc(taskRef, {
      status:                finalStatus,
      completion_type:       payload.completion_type || COMPLETION_TYPE.COMPLETE,
      service_report:        payload.remarks         || "",
      issue_found:           payload.issue_found     || "",
      resolution:            payload.resolution      || "",
      // D-298: MERGE, never replace — photos / defective items added
      // before the offline Complete must survive the sync (same as the
      // online endJob()). Replacing them wiped the job's photos.
      defective_items:       [...(data.defective_items || []), ...(payload.defective_items || [])],
      return_items:          [...(data.return_items    || []), ...(payload.return_items    || [])],
      photos:                [...(data.photos          || []), ...(payload.photos          || [])],
      completed_at:          serverTimestamp(),
      updated_at:            serverTimestamp(),
      duration_minutes:      increment(thisVisitDurationMinutes),
      synced_from_offline:   true,
      offline_completed_at:  endISO,
      completed_by:          payload.filled_by      || payload.uid  || "",
      completed_by_name:     payload.filled_by_name || payload.name || "",
      completed_by_role:     payload.filled_by_role || "",
      filled_by:             payload.filled_by      || "",
      filled_by_name:        payload.filled_by_name || "",
      filled_by_role:        payload.filled_by_role || "", 
      site_payment_received: Boolean(payload.site_payment_received),
      site_payment_amount:   payload.site_payment_received ? Number(payload.site_payment_amount || 0) : 0,
      site_payment_mode:     payload.site_payment_received ? (payload.site_payment_mode || "") : "",
      site_payment_by:       payload.name || "",
      site_payment_at:       payload.site_payment_received ? serverTimestamp() : null,
    });

    // Cash notification
    if (payload.site_payment_received && Number(payload.site_payment_amount) > 0) {
      try {
        const { notifyCashCollected } = await import("./notificationService");
        await notifyCashCollected({
          service_id:  service_id,
          tech_name:   payload.name         || "",
          client_name: data.client_name     || "",
          site_name:   data.site_name       || "",
          amount:      Number(payload.site_payment_amount),
          mode:        payload.site_payment_mode || "cash",
          company_id:  payload.company_id || data.company_id || "",
        });
      } catch (e) {
        console.warn("Cash notification failed:", e);
      }
    }

    await logStatusChange(service_id, {
      company_id:      data.company_id,
      from:            data.status,
      to:              finalStatus,
      changed_by:      payload.filled_by      || payload.uid,
      changed_by_name: payload.filled_by_name || payload.name,
      reason:          "Synced from offline",
    });
    return;
  }
};

// ─────────────────────────────────────────────
// MAIN SYNC FUNCTION
// ─────────────────────────────────────────────
export const runSync = async () => {
  if (!navigator.onLine) {
    console.log("[Sync] Offline — skipping");
    return { synced: 0, failed: 0 };
  }

  console.log("[Sync] Starting...");
  let synced = 0;
  let failed = 0;

  // 1. Sync actions
  const actions = await getUnsyncedActions();
  for (const action of actions) {
    if ((action.retry_count || 0) >= MAX_RETRIES) {
      console.warn("[Sync] Max retries — skipping:", action.id);
      failed++;
      continue;
    }
    try {
      await syncAction(action);
      await markActionSynced(action.id);
      synced++;
      console.log(`[Sync] ✅ ${action.action} synced for ${action.service_id}`);
    } catch (err) {
      await incrementRetry(action.id);
      failed++;
      console.error(`[Sync] ❌ action ${action.id} failed:`, err.message);
    }
  }

  // 2. Sync reports (defective items, remarks saved mid-job)
  const reports = await getUnsyncedReports();
  for (const report of reports) {
    try {
      const ref = doc(db, TASKS, report.service_id);
      await updateDoc(ref, {
        offline_report: {
          remarks:         report.remarks         || "",
          defective_items: report.defective_items || [],
          updated_at:      report.updated_at,
        },
        updated_at: serverTimestamp(),
      });
      await markReportSynced(report.service_id);
      synced++;
    } catch (err) {
      failed++;
      console.error(`[Sync] ❌ report ${report.service_id} failed:`, err.message);
    }
  }

  console.log(`[Sync] Done — ${synced} synced, ${failed} failed`);
  return { synced, failed };
};

// ─────────────────────────────────────────────
// SETUP LISTENERS — call once in App.js
// ─────────────────────────────────────────────
export const setupOfflineSync = () => {
  window.addEventListener("online", async () => {
    console.log("[Sync] Network restored — syncing...");
    await runSync();
  });

  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "visible" && navigator.onLine) {
      await runSync();
    }
  });

  console.log("[Sync] Offline sync listeners registered");
};